export interface Cue {
	start: number
	end: number
	text: string
}

// Parse a WebVTT timestamp (HH:MM:SS.mmm or MM:SS.mmm) into seconds.
const parseTimestamp = (raw: string): number | null => {
	const m = raw.trim().match(/^(?:(\d+):)?(\d{1,2}):(\d{2})[.,](\d{1,3})$/)
	if (!m) return null
	const [, hh, mm, ss, ms] = m
	return (hh ? Number(hh) * 3600 : 0) + Number(mm) * 60 + Number(ss) + Number(ms.padEnd(3, '0')) / 1000
}

/**
 * Parse a WebVTT document into cues. Lenient by design: identifiers, NOTE blocks,
 * styling, and malformed lines are skipped; bad input yields an empty array.
 *
 * Kept pure and dependency-free so video subtitle rendering can reuse it.
 */
export const parseWebVTT = (vtt: string | undefined): Cue[] => {
	if (!vtt) return []
	// Normalise CRLF and bare-CR (old-Mac / some exporters) line endings to LF;
	// only handling \r\n would leave a lone \r in the string, collapsing the whole
	// document into one line so nothing parses.
	const blocks = vtt.replace(/\r\n?/g, '\n').split(/\n\s*\n/)
	const cues: Cue[] = []

	for (const block of blocks) {
		const lines = block.split('\n').map(l => l.trimEnd())
		const arrowIndex = lines.findIndex(l => l.includes('-->'))
		if (arrowIndex === -1) continue

		const [startRaw, endRaw] = lines[arrowIndex].split('-->')
		const start = parseTimestamp(startRaw ?? '')
		// endRaw can carry trailing cue settings (e.g. "line:0%"); take the first token.
		const end = parseTimestamp((endRaw ?? '').trim().split(/\s+/)[0] ?? '')
		if (start === null || end === null) continue

		const text = lines
			.slice(arrowIndex + 1)
			.join(' ')
			.trim()
		if (!text) continue
		cues.push({ start, end, text })
	}

	return cues
}
